import React, { useState, useEffect, useMemo } from "react";
import Modal from "../../Modal";
import styled from "@emotion/styled";
import SelectWithLabel, { YesNoSelect } from "../../../Inputs/Select";
import { isMobile } from "react-device-detect";
import { Alert } from "../../Alert";
import TextInputWithLabel from "../../../Inputs/TextInput";
import CustomDatePicker from '../../../Inputs/DatePicker';
import ToggleButton from "../../../Inputs/ToggleButton";
import { Heading, Paragraph } from "../../../StyledComponents/Forms/Forms";
import { fetchTitles } from "../../../../http/account.js";
import { LoadingScreen } from "../../../StyledComponents/Forms/Loader.jsx";
import { PrescriptionTableRedesignTwoColumn, StandardPrescriptionTableRedesignTwoColumn } from "../../Opticians/PrescriptionTable";
import {
    processEyeTicket,
    fetchApprovedFrames,
    fetchAllowedLensDropDownSelections,
    fetchLensBrand,
    fetchCoatsAndTintsForLens,
    fetchPrescriptionMeasures,
    fetchEyewearTypes,
    fetchEmployeeRequirements,
    processNonEyeTicketRequest,
    fetchLensDropDownSelections
} from "../../../../http/managedClinics.js";
import RichTextBoxWithLabel from "../../../Inputs/RichTextBoxWithLabel";
import {
    StyledPagination,
    InputContainer,
    Label,
    StyledSelect,
    StyledButton,
    StyledSearchInput,
} from "../../../StyledComponents/Table/TableComponents";
import { Row, FieldContainer } from "../../../StyledComponents/Forms/Forms";
import FrameModal from '../../PrivateOrders/FrameModal';
import { isValidUKPostcode } from '../../../Helpers/postcodeFns';
import { StyledModal } from "../../../StyledComponents/Modals/Modals.jsx";

const SmallStyledModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 80%;
  max-width: 500px;
`;

const SmallModalHeader = styled.h3`
  margin-top: 0;
  color: #333;
  text-align: center;
`;

const SmallModalBody = styled.div`
  margin: 20px 0;
`;

const WarningBox = styled.div`
  background-color: #fff3cd;
  border-left: 8px solid #ffeeba;
  padding: 15px;
  margin-bottom: 20px;
`;

const SmallModalFooter = styled.div`
  text-align: right;
`;


const FrameCardContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const FrameCard = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #fff;
`;

const FrameImageContainer = styled.div`
  flex: 0 1 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: #FFFFFF;
`;

const FrameContentContainer = styled.div`
  padding: 20px;
  flex: 0 1 60%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const FrameName = styled.h3`
  margin: 0;
  margin-bottom: 15px;
  font-size: 1.3em;
  color: #333;
`;

const FrameDetail = styled.p`
  margin: 5px 0;
  color: #666;
  font-size: 0.8em;
`;

const FrameDetailTitle = styled.span`
  font-weight: bold;
  color: #333;
`;


const PrescriptionContainerTwoColumn = styled.div`
  display: flex;
  justify-content: space-between;
  width: ${isMobile ? "100%" : "50%"};
  float: right;
`;

const PrescriptionContainerLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: ${isMobile ? "100%" : "50%"};
  float: left;
  overflow: hidden;
`;

const StepsHeader = styled.div`
  background-color: ${({ backGroundColour }) => backGroundColour};
  color: ${({ fontColour }) => fontColour};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-shadow: 0 4px 4px -2px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const Step = styled.div`
  width: 30px;
  height: 30px;
  line-height: 30px;
  border-radius: 15px;
  background: #cccccc;
  color: white;
  text-align: center;
  font-size: 0.9em;
`;

const ActiveStep = styled(Step)`
  background-color: #4caf50;
`;

const StepLabel = styled.span`
  display: inline-block;
  padding: 0 ${isMobile ? "4px" : "15px"};
  color: ${({ fontColour }) => fontColour};
`;

const StepContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ModalBody = styled.div`
  max-height: ${isMobile ? "500px" : "720px"};
  overflow-y: auto;
  width: 100%;
  padding: 0px 40px ${isMobile ? "0px" : "30px"} 40px;
  flex-grow: 1;
`;

const ModalFooter = styled.div`
  text-align: right;
  flex-shrink: 0;
  padding: 10px 0px;
`;

const CardContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  padding-bottom: 20px;
`;

const ImageContainer = styled.div`
  flex: 0 0 150px;
  height: 115px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: #FFFFFF;
`;

const Card = styled.div`
  display: flex;
  width: 100%;
  max-width: 400px;
  height: ${isMobile ? "150px" : "115px"};
  border: 2px solid ${({ isSelected }) => (isSelected ? "#4caf50" : "#ddd")};
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  background: #fff;
  transition: transform 0.2s, border-color 0.3s;

  &:hover {
    transform: scale(1.02);
    cursor: pointer;
  }
`;

const ContentContainer = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
`;

const ButtonSection = styled.div`
  flex: 0 0 50px;
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: center;
  background-color: #e7f3ff;
  border-left: 1px solid #ddd;

  &:hover {
    background-color: #b0d7ff;
  }
`;

const InfoButton = styled.button`
  background-color: transparent;
  border: none;
  font-size: 1.5em;
  color: #2e96ff;
  cursor: pointer;
`;


const CancelButton = styled.button`
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  margin-right: 10px;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #5a6268;
  }
`;

const Button = styled.button`
  background-color: #93c572;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 10px 20px;
  margin-right: 10px;

  &:hover {
    background-color: #698d51;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const defaultEyeTicket = {
    endUserUnqID: 0,
    managedClinicPatient: {
        title: "",
        firstName: "",
        secondName: "",
        mobile: null,
        email: null,
        receiveConfirmation: true,
        eyewearType: "",
        employeeRequirements: ""
    },
    frameID: 0,
    requireSideshields: "Yes",
    lenseID: 0,
    requestedDifferentLensType: false,
    requestedNonStandardLens: false,
    lenseCoatID: 0,
    requestedUnAuthorisedCoating: false,
    lenseTintID: 0,
    requestedUnAuthorisedTint: false,
    reasonForUnauthorisedItem: "",
    casesID: 0,
    prescription: {
        rightEye: {
            sphere: "0.00",
            cylinder: "0.00",
            axis: "0.00",
            hPrism: null,
            hPrismDirection: "-",
            vPrism: null,
            vPrismDirection: "-",
            distanceOC: "0.00",
            readOC: "0.00",
            intermediateOC: "0.00",
            add: null,
            heights: null
        },
        leftEye: {
            sphere: "0.00",
            cylinder: "0.00",
            axis: "0.00",
            hPrism: null,
            hPrismDirection: "-",
            vPrism: null,
            vPrismDirection: "-",
            distanceOC: null,
            readOC: "0.00",
            intermediateOC: "0.00",
            add: "0.00",
            heights: null
        },
        svDistOrRead: 0
    },
    rxNotes: "",
    permittedLenseType: "",
    permittedLenseID: 0,
    standardLensPrescription: {
        rightEye: {
            sphere: "0.00",
            cylinder: "0.00",
            axis: "0.00",
            hPrism: null,
            hPrismDirection: "-",
            vPrism: null,
            vPrismDirection: "-",
            distanceOC: "0.00",
            readOC: "0.00",
            intermediateOC: "0.00",
            add: null,
            heights: null
        },
        leftEye: {
            sphere: "0.00",
            cylinder: "0.00",
            axis: "0.00",
            hPrism: null,
            hPrismDirection: "-",
            vPrism: null,
            vPrismDirection: "-",
            distanceOC: "0.00",
            readOC: "0.00",
            intermediateOC: "0.00",
            add: null,
            heights: null
        },
        svDistOrRead: 0
    },
    postageandPackingOption: 4,
    rxShippingOption: 0,
    deliveryAddress: {
        companyname: "",
        coaddress1: "",
        coaddress2: "",
        coaddress3: "",
        coaddress4: "",
        copostcode: ""
    },
    dateOfSightTest: new Date(),
    sightTestPerformed: 0,
    sightTestInterval: 24
};

const cloneDefaultEyeTicket = () => JSON.parse(JSON.stringify(defaultEyeTicket));

export default function RaiseProcessEyeTicket({ showHideModal, isModalOpen, colours, managedClinic }) {
    const [eyeTicketDetails, setEyeTicketDetails] = useState(cloneDefaultEyeTicket());
    const [missingFields, setMissingFields] = useState({});
    const [invalidPostcode, setInvalidPostcode] = useState('');

    const stepTitles = useMemo(() => {
        return eyeTicketDetails.requestedDifferentLensType || eyeTicketDetails.requestedNonStandardLens ? ["1", "2", "3", "4", "5"] : ["1", "2", "3", "4"];
    }, [eyeTicketDetails]);

    const stepDescriptions = useMemo(() => {
        return eyeTicketDetails.requestedDifferentLensType || eyeTicketDetails.requestedNonStandardLens ?
            ["Employee Details", "Frames", "Prescription", "Standard Prescription", "Order Details"] :
            ["Employee Details", "Frames", "Prescription", "Order Details"];
    }, [eyeTicketDetails]);

    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [frames, setFrames] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");
    const [materialFilter, setMaterialFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState((isMobile ? 6 : 9));
    const [arrPageSizes] = useState((isMobile ? [6, 12, 24, 48] : [9, 18, 36, 72]));
    const [titles, setTitles] = useState([]);

    const [showFrameDetails, setShowFrameDetails] = useState(false);
    const [selectedFrameInfo, setSelectedFrameInfo] = useState(null);

    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [selectedType, setSelectedType] = useState(null);

    const [lensMaterials, setLensMaterials] = useState(null);
    const [lensTypes, setLensTypes] = useState(null);
    const [defaultLensTypes, setDefaultLensTypes] = useState(null);

    const [defaultLensBrands, setDefaultLensBrands] = useState([]);
    const [lensBrands, setLensBrands] = useState([]);

    const [defaultSvTypes, setDefaultSvTypes] = useState([]);
    const [svTypes, setSvTypes] = useState([]);

    const [zeroSphCheck, setZeroSphCheck] = useState(false);
    const [zeroSphCheckConfirmed, setZeroSphCheckConfirmed] = useState(false);

    const [defaultSvType, setDefaultSvType] = useState(null);
    const [svType, setSvType] = useState(null);

    const [lensCoatings, setLensCoatings] = useState([]);
    const [lensTints, setLensTints] = useState([]);
    const [lensCases, setLensCases] = useState([]);

    const [defaultPrescriptionMeasures, setDefaultPrescriptionMeasures] = useState(null);
    const [prescriptionMeasures, setPrescriptionMeasures] = useState(null);
    const [eyewearOptions, setEyewearOptions] = useState([]);
    const [invalidPromoCode, setInvalidPromoCode] = useState(false);

    const [overrideFrame, setOverrideFrame] = useState(false);

    const [isDeliveryAlternate, setIsDeliveryAlternate] = useState(false);

    const [eyewearTypes, setEyewearTypes] = useState([]);
    const [employeeRequirements, setEmployeeRequirements] = useState([]);

    useEffect(() => {
        const resetPrescription = () => {
            ['RightEye', 'LeftEye'].forEach((eye) => {
                handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.add`, null);
                handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.heights`, null);
                handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrism`, null);
                handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrismDirection`, "-");
                handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrism`, null);
                handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrismDirection`, "-");
                handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.distanceOC`, "0.00");
                handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.readOC`, "0.00");
                handleEnteredValues(`prescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.intermediateOC`, "0.00");
            });
        };

        resetPrescription();
    }, [selectedType]);

    useEffect(() => {
        const resetStandardPrescription = () => {
            ['RightEye', 'LeftEye'].forEach((eye) => {
                handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.add`, null);
                handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.heights`, null);
                handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrism`, null);
                handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.hPrismDirection`, "-");
                handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrism`, null);
                handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.vPrismDirection`, "-");
                handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.distanceOC`, "0.00");
                handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.readOC`, "0.00");
                handleEnteredValues(`standardLensPrescription.${eye === 'RightEye' ? 'rightEye' : 'leftEye'}.intermediateOC`, "0.00");
            });
        };

        resetStandardPrescription();
    }, [eyeTicketDetails.permittedLenseType]);



    function handleEnteredValues(identifier, value) {
        setEyeTicketDetails((prevValues) => {
            const keys = identifier.split('.');
            const lastKey = keys.pop();
            const deepClone = { ...prevValues };

            let nestedObject = deepClone;
            keys.forEach((key) => {
                if (!nestedObject[key]) nestedObject[key] = {};
                nestedObject = nestedObject[key];
            });
            if (lastKey === "distanceOC") {
                const readOcValue = Number(value) - 1.5;
                nestedObject["readOC"] = readOcValue.toFixed(2).toString();
            }

            nestedObject[lastKey] = value;

            return deepClone;
        });
    }

    const handleProcessEyeTicket = async (glassesRequired = true) => {
        let isComplete = true;
        const isValid = validateForm();

        if (!isValid) {
            return;
        }

        if (!glassesRequired) {
            setIsLoading(true);
            const preparedEyeTicket = {
                title: eyeTicketDetails.managedClinicPatient.title,
                firstName: eyeTicketDetails.managedClinicPatient.firstName,
                secondName: eyeTicketDetails.managedClinicPatient.secondName,
                mobile: eyeTicketDetails.managedClinicPatient.mobile || "0",
                email: eyeTicketDetails.managedClinicPatient.email || "0",
                receiveConfirmation: (!eyeTicketDetails.managedClinicPatient.mobile && !eyeTicketDetails.managedClinicPatient.email ? false : true),
                eyewearType: eyeTicketDetails.managedClinicPatient.eyewearType,
                employeeRequirements: eyeTicketDetails.managedClinicPatient.employeeRequirements,
            };

            try {
                await processNonEyeTicketRequest(managedClinic.EndUserUnqID, managedClinic.EndUserAccountID, preparedEyeTicket);
                setError(null);
            } catch (e) {
                isComplete = false;
                setError(e);
            } finally {
                setEyeTicketDetails(cloneDefaultEyeTicket());
                setIsLoading(false);
                showHideModal();
            }
        } else {

            setIsLoading(true);
            setCurrentStep(999);

            const preparedEyeTicket = {
                ...eyeTicketDetails,
                prescription: {
                    ...eyeTicketDetails.prescription,
                    svDistOrRead: svType === 'Distance' || selectedType !== "Single Vision" ? 0 : svType === 'Readers' ? 1 : 2,
                },
                standardLensPrescription: {
                    ...eyeTicketDetails.standardLensPrescription,
                    svDistOrRead: defaultSvType === 'Distance' || eyeTicketDetails.permittedLenseType !== "Single Vision" ? 0 : defaultSvType === 'Readers' ? 1 : 2,
                },
                managedClinicPatient: {
                    ...eyeTicketDetails.managedClinicPatient,
                    mobile: eyeTicketDetails.managedClinicPatient.mobile || "0",
                    email: eyeTicketDetails.managedClinicPatient.email || "0",
                },
                endUserUnqID: managedClinic.EndUserUnqID,
                rxShippingOption: isDeliveryAlternate ? 1 : 0,
            };

            delete preparedEyeTicket.permittedLenseType;

            try {
                const response = await processEyeTicket(preparedEyeTicket);
                setError(null);
            } catch (e) {
                isComplete = false;
                setError(e);
            } finally {
                setIsLoading(false);

            }

            if (isComplete) {
                showHideModal();
                setEyeTicketDetails(defaultEyeTicket);
            }
        }
    };

    const validateForm = () => {
        let newMissingFields = {};
        let newInvalidData = {};
        let isValid = true;


        if (currentStep === 0) {
            if (eyeTicketDetails.managedClinicPatient.title === "") { newMissingFields.title = true; }
            if (eyeTicketDetails.managedClinicPatient.firstName === "") { newMissingFields.firstName = true; }
            if (eyeTicketDetails.managedClinicPatient.secondName === "") { newMissingFields.secondName = true; }
            if (eyeTicketDetails.managedClinicPatient.eyewearType === "") { newMissingFields.eyewearType = true; }
            if (eyeTicketDetails.managedClinicPatient.employeeRequirements === "") { newMissingFields.employeeRequirements = true; }

        }

        if (currentStep === 1 && eyeTicketDetails.frameID === 1) {
            newMissingFields.frameID = true;
        }

        if (currentStep === 2) {
            if (!eyeTicketDetails.lenseID || eyeTicketDetails.lenseID === 0) { newMissingFields.lenseID = true; }
            if (!selectedType || selectedType === 0) { newMissingFields.selectedType = true; }

            if ((selectedType === "Bifocal" || selectedType === "Progressive") || selectedType === "Single Vision" && svType !== "Intermediate") {
                if (!eyeTicketDetails.prescription.rightEye.distanceOC || eyeTicketDetails.prescription.rightEye.distanceOC === "0.00") { newMissingFields.RightDistanceOC = true; }
                if (!eyeTicketDetails.prescription.leftEye.distanceOC || eyeTicketDetails.prescription.leftEye.distanceOC === "0.00") { newMissingFields.LeftDistanceOC = true; }
                if (!eyeTicketDetails.prescription.rightEye.readOC || eyeTicketDetails.prescription.rightEye.readOC === "0.00") { newMissingFields.RightReadOC = true; }
                if (!eyeTicketDetails.prescription.leftEye.readOC || eyeTicketDetails.prescription.leftEye.readOC === "0.00") { newMissingFields.LeftReadOC = true; }
            }

            if (selectedType === "Single Vision" && svType === "Intermediate") {
                if (!eyeTicketDetails.prescription.rightEye.intermediateOC || eyeTicketDetails.prescription.rightEye.intermediateOC === "0.00") { newMissingFields.RightIntermediateOC = true; }
                if (!eyeTicketDetails.prescription.leftEye.intermediateOC || eyeTicketDetails.prescription.leftEye.intermediateOC === "0.00") { newMissingFields.LeftIntermediateOC = true; }
            }

            if (selectedType === "Single Vision" || (selectedType === "Bifocal" || selectedType === "Progressive")) {
                if (!eyeTicketDetails.prescription.rightEye.sphere) { newMissingFields.RightSphere = true; }
                if (!eyeTicketDetails.prescription.leftEye.sphere) { newMissingFields.LeftSphere = true; }

                if (!eyeTicketDetails.prescription.rightEye.cylinder) { newMissingFields.RightCylinder = true; }
                if (!eyeTicketDetails.prescription.leftEye.cylinder) { newMissingFields.LeftCylinder = true; }

                if (!eyeTicketDetails.prescription.rightEye.axis) { newMissingFields.RightAxis = true; }
                if (!eyeTicketDetails.prescription.leftEye.axis) { newMissingFields.LeftAxis = true; }
            }

            if (selectedType === "Bifocal" || selectedType === "Progressive") {
                if (!eyeTicketDetails.prescription.rightEye.add) { newMissingFields.RightAdd = true; }
                if (!eyeTicketDetails.prescription.leftEye.add) { newMissingFields.LeftAdd = true; }

                if (!eyeTicketDetails.prescription.rightEye.heights) { newMissingFields.RightHeights = true; }
                if (!eyeTicketDetails.prescription.leftEye.heights) { newMissingFields.LeftHeights = true; }
            }
        }

        if ((eyeTicketDetails.requestedDifferentLensType || eyeTicketDetails.requestedNonStandardLens) && currentStep === 3) {
            if (!eyeTicketDetails.lenseID || eyeTicketDetails.permittedLenseID === 0) { newMissingFields.permittedLenseID = true; }
            if (!eyeTicketDetails.permittedLenseType || eyeTicketDetails.permittedLenseType === 0) { newMissingFields.permittedLenseType = true; }

            if ((eyeTicketDetails.permittedLenseType === "Bifocal" || eyeTicketDetails.permittedLenseType === "Progressive") || eyeTicketDetails.permittedLenseType === "Single Vision" && defaultSvType !== "Intermediate") {
                if (!eyeTicketDetails.standardLensPrescription.rightEye.distanceOC || eyeTicketDetails.standardLensPrescription.rightEye.distanceOC === "0.00") { newMissingFields.RightDistanceOC = true; }
                if (!eyeTicketDetails.standardLensPrescription.leftEye.distanceOC || eyeTicketDetails.standardLensPrescription.leftEye.distanceOC === "0.00") { newMissingFields.LeftDistanceOC = true; }
                if (!eyeTicketDetails.standardLensPrescription.rightEye.readOC || eyeTicketDetails.standardLensPrescription.rightEye.readOC === "0.00") { newMissingFields.RightReadOC = true; }
                if (!eyeTicketDetails.standardLensPrescription.leftEye.readOC || eyeTicketDetails.standardLensPrescription.leftEye.readOC === "0.00") { newMissingFields.LeftReadOC = true; }
            }

            if (eyeTicketDetails.permittedLenseType === "Single Vision" && defaultSvType === "Intermediate") {
                if (!eyeTicketDetails.standardLensPrescription.rightEye.intermediateOC || eyeTicketDetails.standardLensPrescription.rightEye.intermediateOC === "0.00") { newMissingFields.RightIntermediateOC = true; }
                if (!eyeTicketDetails.standardLensPrescription.leftEye.intermediateOC || eyeTicketDetails.standardLensPrescription.leftEye.intermediateOC === "0.00") { newMissingFields.LeftIntermediateOC = true; }
            }

            if (eyeTicketDetails.permittedLenseType === "Single Vision" || (eyeTicketDetails.permittedLenseType === "Bifocal" || eyeTicketDetails.permittedLenseType === "Progressive")) {
                if (!eyeTicketDetails.standardLensPrescription.rightEye.sphere) { newMissingFields.RightSphere = true; }
                if (!eyeTicketDetails.standardLensPrescription.leftEye.sphere) { newMissingFields.LeftSphere = true; }

                if (!eyeTicketDetails.standardLensPrescription.rightEye.cylinder) { newMissingFields.RightCylinder = true; }
                if (!eyeTicketDetails.standardLensPrescription.leftEye.cylinder) { newMissingFields.LeftCylinder = true; }

                if (!eyeTicketDetails.standardLensPrescription.rightEye.axis) { newMissingFields.RightAxis = true; }
                if (!eyeTicketDetails.standardLensPrescription.leftEye.axis) { newMissingFields.LeftAxis = true; }
            }

            if (eyeTicketDetails.permittedLenseType === "Bifocal" || eyeTicketDetails.permittedLenseType === "Progressive") {
                if (!eyeTicketDetails.standardLensPrescription.rightEye.add) { newMissingFields.RightAdd = true; }
                if (!eyeTicketDetails.standardLensPrescription.leftEye.add) { newMissingFields.LeftAdd = true; }

                if (!eyeTicketDetails.standardLensPrescription.rightEye.heights) { newMissingFields.RightHeights = true; }
                if (!eyeTicketDetails.standardLensPrescription.leftEye.heights) { newMissingFields.LeftHeights = true; }
            }
        }

        if (currentStep === ((eyeTicketDetails.requestedDifferentLensType || eyeTicketDetails.requestedNonStandardLens) ? 4 : 3)) {
            if (eyeTicketDetails.sightTestPerformed === 1) {
                if (eyeTicketDetails.dateOfSightTest === "") { newMissingFields.dateOfSightTest = true; }
            }

            if (isDeliveryAlternate) {
                if (eyeTicketDetails.deliveryAddress.companyname === "") { newMissingFields.companyname = true; }
                if (eyeTicketDetails.deliveryAddress.coaddress1 === "") { newMissingFields.coaddress1 = true; }
                if (eyeTicketDetails.deliveryAddress.coaddress2 === "") { newMissingFields.coaddress2 = true; }
                if (eyeTicketDetails.deliveryAddress.coaddress3 === "") { newMissingFields.coaddress3 = true; }
                if (eyeTicketDetails.deliveryAddress.copostcode === "") { newMissingFields.copostcode = true; }

                if (!isValidUKPostcode(eyeTicketDetails.deliveryAddress.copostcode)) {
                    setInvalidPostcode('Please enter a valid UK postcode');
                    isValid = false;
                }
            }
        }

        for (let key in newMissingFields) {
            if (newMissingFields[key] === true) {
                isValid = false;
                break;
            }
        }

        if (currentStep === 2 && !zeroSphCheckConfirmed && isValid &&
            (
                eyeTicketDetails.prescription.rightEye.sphere === "0.00"
                || eyeTicketDetails.prescription.leftEye.sphere === "0.00"
            )
        ) {
            setZeroSphCheck(true);
            isValid = false;
        }

        setMissingFields(newMissingFields);
        return isValid;
    };

    const zeroSphModal = zeroSphCheck && (
        <SmallStyledModal>
            <SmallModalHeader>Zero Sphere?</SmallModalHeader>
            <ModalBody>
                <WarningBox>
                    <strong>Zero Sphere Value</strong>
                    <p>You have entered the value '0.00' for either the left, or right eye sphere. Is this correct?</p>
                </WarningBox>
            </ModalBody>
            <ModalFooter>
                <CancelButton onClick={() => setZeroSphCheck(false)}>No</CancelButton>
                <Button onClick={() => {
                    setZeroSphCheckConfirmed(true);
                }}>Yes</Button>
            </ModalFooter>
        </SmallStyledModal>
    );

    const nextStep = () => {
        if (validateForm()) {
            if (currentStep === 0 && eyeTicketDetails.managedClinicPatient.employeeRequirements !== 'Require Glasses') {
                handleProcessEyeTicket(false);
            } else if (currentStep < stepTitles.length - 1) {
                setCurrentStep(currentStep + 1);
            }
        }
    };

    const deliveryAddressOptions = [
        { label: 'Optician Address', value: 0 },
        { label: 'Alternative Address', value: 1 }
    ];

    const sightTestDateOptions = [
        { label: '6 Months', value: 6 },
        { label: '12 Months', value: 12 },
        { label: '24 Months', value: 24 }
    ];

    useEffect(() => {
        const loadEyewearTypes = async () => {
            setIsLoading(true);
            try {
                const eyewearTypes = await fetchEyewearTypes(managedClinic.EndUserUnqID);

                const restructuredEyewearTypes = [{ label: 'Select an Eyewear Type..', value: '' }];

                restructuredEyewearTypes.push(...eyewearTypes["Eyewear Type"].map(eyewearType => ({
                    label: eyewearType,
                    value: eyewearType
                })));

                setEyewearTypes(restructuredEyewearTypes);
                setError(null);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        }

        const loadTitles = async () => {
            setIsLoading(true);
            try {
                const titles = await fetchTitles();

                const restructuredTitles = [{ label: 'Select a title..', value: '' }];

                restructuredTitles.push(...titles.map(title => ({
                    label: title.title,
                    value: title.title
                })));

                setTitles(restructuredTitles);
                setError(null);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        };

        loadTitles();
        loadEyewearTypes();
    }, [managedClinic]);

    useEffect(() => {
        const loadEmployeeRequirements = async () => {
            setIsLoading(true);
            if (eyeTicketDetails.managedClinicPatient.eyewearType) {
                try {
                    const employeeRequirements = await fetchEmployeeRequirements(managedClinic.EndUserUnqID, eyeTicketDetails.managedClinicPatient.eyewearType);
                    const restructuredEmployeeRequirements = [{ label: 'Select Employee Requirement', value: '' }];

                    restructuredEmployeeRequirements.push(...employeeRequirements["Employee Requirements"].map(requirement => ({
                        label: requirement,
                        value: requirement
                    })));

                    setEmployeeRequirements(restructuredEmployeeRequirements);
                    setError(null);
                } catch (e) {
                    setError(e);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        loadEmployeeRequirements();
    }, [eyeTicketDetails.managedClinicPatient.eyewearType]);

    useEffect(() => {
        const loadFrames = async () => {
            setIsLoading(true);
            if (eyeTicketDetails.managedClinicPatient.eyewearType) {
                try {
                    const framesData = await fetchApprovedFrames(managedClinic.EndUserUnqID, eyeTicketDetails.managedClinicPatient.eyewearType);
                    setFrames(framesData.lstFramePack || []);
                    setError(null);
                } catch (e) {
                    setError(e);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        loadFrames();
    }, [eyeTicketDetails.managedClinicPatient.eyewearType]);

    const loadLensMaterialsAndTypes = async () => {
        if (eyeTicketDetails.frameID && eyeTicketDetails.frameID !== 0) {
            setIsLoading(true);
            try {
                const lensOptions = await fetchLensDropDownSelections(managedClinic.EndUserUnqID, eyeTicketDetails.frameID, eyeTicketDetails.requestedDifferentLensType);

                const restructuredMaterials = [{ label: 'Select a lens material..', value: '' }];
                restructuredMaterials.push(...lensOptions["Lens Material"].map(material => ({
                    label: material,
                    value: material
                })));

                const restructuredTypes = [{ label: 'Select a lens type..', value: '' }];
                restructuredTypes.push(...lensOptions["Lens Type"].map(type => ({
                    label: type,
                    value: (type === "D28 Bifocal" ? "Bifocal" : type)
                })));

                setLensMaterials(restructuredMaterials || []);

                if (restructuredMaterials.length === 2) {
                    setSelectedMaterial(restructuredMaterials[1].value);
                }
                setLensTypes(restructuredTypes || []);
                setError(null);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        loadLensMaterialsAndTypes();
    }, [eyeTicketDetails.frameID, eyeTicketDetails.requestedDifferentLensType]);

    useEffect(() => {
        const loadDefaultLensMaterialsAndTypes = async () => {
            if (eyeTicketDetails.frameID && eyeTicketDetails.frameID !== 0) {
                setIsLoading(true);
                try {
                    const lensOptions = await fetchLensDropDownSelections(managedClinic.EndUserUnqID, eyeTicketDetails.frameID);

                    const restructuredTypes = [{ label: 'Select a lens type..', value: '' }];
                    restructuredTypes.push(...lensOptions["Lens Type"].map(type => ({
                        label: type,
                        value: (type === "D28 Bifocal" ? "Bifocal" : type)
                    })));

                    setDefaultLensTypes(restructuredTypes || []);
                    setError(null);
                } catch (e) {
                    setError(e);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        loadDefaultLensMaterialsAndTypes();
    }, [eyeTicketDetails.frameID]);

    const loadLensTintsAndCoatings = async () => {
        if (eyeTicketDetails.lenseID) {
            setIsLoading(true);
            try {
                const response = await fetchCoatsAndTintsForLens(managedClinic.EndUserUnqID, eyeTicketDetails.lenseID, !eyeTicketDetails.requestedUnAuthorisedTint, !eyeTicketDetails.requestedUnAuthorisedCoating);

                if (response.Coats) {
                    const restructuredCoats = [{ label: 'Select a lens coating..', value: '0' }];
                    restructuredCoats.push(...response.Coats.map(coat => ({
                        label: coat.descrip,
                        value: coat.unid
                    })));

                    setLensCoatings(restructuredCoats || []);
                } else {
                    setLensCoatings([{
                        label: 'None',
                    }]);;
                }

                if (response.Cases) {
                    const restructuredCases = [];
                    restructuredCases.push(...response.Cases.map(cases => ({
                        label: cases.descrip,
                        value: cases.unid
                    })));

                    setLensCases(restructuredCases || []);
                    handleEnteredValues('casesID', restructuredCases[0].value);
                } else {
                    setLensCases([{
                        label: 'None',
                    }]);
                }

                if (response.Tints) {
                    const restructuredTints = [{ label: 'Select a lens tint..', value: '0' }];
                    restructuredTints.push(...response.Tints.map(tint => ({
                        label: tint.descrip,
                        value: tint.unid
                    })));

                    setLensTints(restructuredTints || []);
                } else {
                    setLensTints([{
                        label: 'None',
                    }]);
                }

                setError(null);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        loadDefaultPrescriptionMeasures();
    }, [eyeTicketDetails.permittedLenseID, eyeTicketDetails.frameID, defaultSvType]);

    useEffect(() => {
        loadPrescriptionMeasures();
    }, [eyeTicketDetails.lenseID, eyeTicketDetails.frameID, svType]);

    const loadPrescriptionMeasures = async () => {
        if (eyeTicketDetails.frameID && eyeTicketDetails.lenseID) {
            setIsLoading(true);
            try {
                const response = await fetchPrescriptionMeasures(eyeTicketDetails.lenseID, eyeTicketDetails.frameID, svType);

                setPrescriptionMeasures(response);

                setError(null);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const loadDefaultPrescriptionMeasures = async () => {
        if (eyeTicketDetails.frameID && eyeTicketDetails.permittedLenseID) {
            setIsLoading(true);
            try {
                const response = await fetchPrescriptionMeasures(eyeTicketDetails.permittedLenseID, eyeTicketDetails.frameID, defaultSvType);

                setDefaultPrescriptionMeasures(response);

                setError(null);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const toggleLensOverrides = () => {
        setEyeTicketDetails((prevDetails) => ({
            ...prevDetails,
            requestedDifferentLensType: !prevDetails.requestedDifferentLensType,
            requestedNonStandardLens: !prevDetails.requestedDifferentLensType,
        }));
    };

    const handleToggle = (field) => {
        setEyeTicketDetails((prevDetails) => ({
            ...prevDetails,
            [field]: !prevDetails[field],
        }));
    };

    const loadLensBrand = async () => {
        if (eyeTicketDetails.frameID && selectedMaterial) {
            setIsLoading(true);
            try {
                const response = await fetchLensBrand(managedClinic.EndUserUnqID, eyeTicketDetails.frameID, selectedType, !eyeTicketDetails.requestedNonStandardLens);

                if (response.length) {
                    if (response[0].SVType) {
                        const restructuredSVTypes = [];
                        restructuredSVTypes.push(...response[0].SVType.map(type => ({
                            label: type,
                            value: type
                        })));

                        setSvTypes(restructuredSVTypes || []);
                    } else {
                        setSvTypes([]);
                    }

                    const restructuredBrands = [{ label: 'Select a lens brand..', value: '' }];
                    restructuredBrands.push(...response.map(brand => ({
                        label: brand.LensBrand,
                        value: brand.lenunidcode
                    })));

                    setLensBrands(restructuredBrands || []);
                }

                setError(null);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const loadDefaultLensBrand = async () => {
        if (eyeTicketDetails.frameID && selectedMaterial) {
            setIsLoading(true);
            try {
                const response = await fetchLensBrand(managedClinic.EndUserUnqID, eyeTicketDetails.frameID);

                if (response.length) {
                    if (response[0].SVType) {
                        const restructuredSVTypes = [];
                        restructuredSVTypes.push(...response[0].SVType.map(type => ({
                            label: type,
                            value: type
                        })));

                        setDefaultSvTypes(restructuredSVTypes || []);
                    } else {
                        setDefaultSvTypes([]);
                    }

                    const restructuredBrands = [{ label: 'Select a lens brand..', value: '' }];
                    restructuredBrands.push(...response.map(brand => ({
                        label: brand.LensBrand,
                        value: brand.lenunidcode
                    })));

                    setDefaultLensBrands(restructuredBrands || []);
                }

                setError(null);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        loadLensTintsAndCoatings();
    }, [eyeTicketDetails.lenseID, eyeTicketDetails.requestedUnAuthorisedCoating, eyeTicketDetails.requestedUnAuthorisedTint]);

    useEffect(() => {
        loadLensBrand();
    }, [selectedType, eyeTicketDetails.requestedDifferentLensType]);

    useEffect(() => {
        loadDefaultLensBrand();
    }, [eyeTicketDetails.permittedLenseType]);


    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm, pageSize, materialFilter]);

    function ShowHideFrameDetails() {
        setShowFrameDetails(!showFrameDetails);
    }

    const handleMoreInfoClick = (event, frame) => {
        event.stopPropagation();
        setSelectedFrameInfo(frame);
        ShowHideFrameDetails();
    };

    const filteredFrames = frames
        .filter((frame) => frame.frameName.toLowerCase().includes(searchTerm.toLowerCase()))
        .filter((frame) => (materialFilter ? frame.frameMaterial === materialFilter : true));

    const paginatedFrames = filteredFrames.slice(
        currentPage * pageSize,
        (currentPage + 1) * pageSize
    );

    return (
        <>
            <Modal open={zeroSphCheck && !zeroSphCheckConfirmed}>
                {zeroSphCheck && !zeroSphCheckConfirmed && zeroSphModal}
            </Modal>
            {showFrameDetails &&
                <FrameModal
                    showHideModal={ShowHideFrameDetails}
                    isModalOpen={showFrameDetails}
                    frame={selectedFrameInfo} />}
            <Modal open={isModalOpen}>
                <StyledModal maxWidth={isMobile ? "975px" : "1500px"}>
                    <StepsHeader backGroundColour={colours.sidebar} fontColour={colours.font}>
                        <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                            {stepTitles.map((number, index) => (
                                <StepContainer key={index}>
                                    {currentStep === index ? <ActiveStep>{number}</ActiveStep> : <Step>{number}</Step>}
                                    <StepLabel fontColour={colours.font}>{stepDescriptions[index]}</StepLabel>
                                </StepContainer>
                            ))}
                        </div>
                    </StepsHeader>
                    {error && !isLoading && <Alert type="error">Unable to process request due to <b>{error.message}</b></Alert>}
                    {isLoading && currentStep === 999 &&
                        <ModalBody>
                            <LoadingScreen
                                value="Please wait whilst we process your EyeTicket, this may take a few moments.."
                                size="60px"
                                borderColor="#d3d3d3"
                                topColor={colours.sidebar}
                            />
                        </ModalBody>
                    }
                    {!isLoading && !error && (
                        <ModalBody>
                            {currentStep === 0 && (
                                <>
                                    <Heading>Employee Details</Heading>
                                    <Paragraph>Please provide information regarding the patient of whom you are raising the EyeTicket on behalf of.</Paragraph>
                                    <FieldContainer>
                                        <SelectWithLabel
                                            label="Title"
                                            value={eyeTicketDetails.managedClinicPatient.title}
                                            options={titles}
                                            required={true}
                                            missing={missingFields?.title}
                                            onChange={(e) => handleEnteredValues('managedClinicPatient.title', e.target.value)} />
                                    </FieldContainer>
                                    <FieldContainer>
                                        <TextInputWithLabel
                                            label='First Name'
                                            required={true}
                                            missing={missingFields?.firstName}
                                            value={eyeTicketDetails.managedClinicPatient.firstName}
                                            onChange={(e) => handleEnteredValues('managedClinicPatient.firstName', e.target.value)} />
                                    </FieldContainer>
                                    <FieldContainer>
                                        <TextInputWithLabel
                                            label='Surname'
                                            required={true}
                                            missing={missingFields?.secondName}
                                            value={eyeTicketDetails.managedClinicPatient.secondName}
                                            onChange={(e) => handleEnteredValues('managedClinicPatient.secondName', e.target.value)} />
                                    </FieldContainer>
                                    <FieldContainer>
                                        <TextInputWithLabel
                                            label='Mobile'
                                            value={eyeTicketDetails.managedClinicPatient.mobile}
                                            onChange={(e) => handleEnteredValues('managedClinicPatient.mobile', e.target.value)} />
                                    </FieldContainer>
                                    <FieldContainer>
                                        <TextInputWithLabel
                                            label='Email'
                                            value={eyeTicketDetails.managedClinicPatient.email}
                                            onChange={(e) => handleEnteredValues('managedClinicPatient.email', e.target.value)} />
                                    </FieldContainer>
                                    <FieldContainer>
                                        <SelectWithLabel
                                            label="Eyewear Type"
                                            value={eyeTicketDetails.managedClinicPatient.eyewearType}
                                            options={eyewearTypes}
                                            required={true}
                                            missing={missingFields?.eyewearType}
                                            onChange={(e) => handleEnteredValues('managedClinicPatient.eyewearType', e.target.value)} />
                                    </FieldContainer>
                                    <FieldContainer>
                                        <SelectWithLabel
                                            label="Employee Requirements"
                                            value={eyeTicketDetails.managedClinicPatient.employeeRequirements}
                                            options={employeeRequirements}
                                            required={true}
                                            missing={missingFields?.employeeRequirements}
                                            onChange={(e) => handleEnteredValues('managedClinicPatient.employeeRequirements', e.target.value)}
                                            disabled={employeeRequirements.length === 0} />
                                    </FieldContainer>
                                </>
                            )}
                            {currentStep === 1 && (
                                <>
                                    <h3>Select a frame</h3>
                                    <Alert type="info">
                                        Click a frame to select it and press <span><b>'Next'</b></span> to enter the prescription. You can click the <span><b>'i'</b></span> icon for more information about each  frame
                                    </Alert>
                                    {frames.length === 0 && <Alert type='error'>No frames were found</Alert>}
                                    <Row>
                                        <InputContainer>
                                            <Label>Search Frames:</Label>
                                            <StyledSearchInput
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                placeholder={`Search ${filteredFrames.length} frames...`}
                                            />
                                        </InputContainer>
                                        <InputContainer>
                                            <Label>Filter by Material:</Label>
                                            <StyledSelect
                                                value={materialFilter}
                                                onChange={(e) => setMaterialFilter(e.target.value)}
                                            >
                                                <option value="">All Materials</option>
                                                {Array.from(new Set(frames.map((frame) => frame.frameMaterial))).map(
                                                    (material, index) => (
                                                        <option key={index} value={material}>
                                                            {material}
                                                        </option>
                                                    )
                                                )}
                                            </StyledSelect>
                                        </InputContainer>
                                    </Row>
                                    <CardContainer>
                                        {paginatedFrames.map((frame, index) => (
                                            <Card
                                                key={`${frame.frameName}-${index}`}
                                                isSelected={eyeTicketDetails.frameID === frame.frameId}
                                                onClick={() => {
                                                    handleEnteredValues('frameID', frame.frameId);
                                                    setSelectedFrameInfo(frame);
                                                }}
                                            >
                                                <ImageContainer>
                                                    <img
                                                        src={`https://www.mcrsafetyeurope.com/${frame.Image_A}`}
                                                        alt="Frame"
                                                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                                    />
                                                </ImageContainer>
                                                <ContentContainer>
                                                    <FrameName>{frame.frameName}</FrameName>
                                                    <FrameDetail>Material: {frame.frameMaterial}</FrameDetail>
                                                    <FrameDetail>Type: {frame.frametype}</FrameDetail>
                                                </ContentContainer>
                                                <ButtonSection onClick={(event) => handleMoreInfoClick(event, frame)}>
                                                    <InfoButton>
                                                        i
                                                    </InfoButton>
                                                </ButtonSection>
                                            </Card>
                                        ))}
                                    </CardContainer>
                                    <StyledPagination>
                                        <InputContainer>
                                            <Label>
                                                Page <strong>{currentPage + 1} of {Math.ceil(filteredFrames.length / pageSize)}</strong>
                                            </Label>
                                            <StyledSelect
                                                value={pageSize}
                                                onChange={(e) => {
                                                    setPageSize(Number(e.target.value));
                                                    setCurrentPage(0);
                                                }}
                                            >
                                                {arrPageSizes.map((size) => (
                                                    <option key={size} value={size}>
                                                        Show {size}
                                                    </option>
                                                ))}
                                            </StyledSelect>
                                        </InputContainer>
                                        <div>
                                            <StyledButton onClick={() => setCurrentPage(0)} disabled={currentPage === 0}>
                                                {'<<'}
                                            </StyledButton>{' '}
                                            <StyledButton onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))} disabled={currentPage === 0}>
                                                {'<'}
                                            </StyledButton>{' '}
                                            <StyledButton onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(filteredFrames.length / pageSize) - 1))} disabled={currentPage === Math.ceil(filteredFrames.length / pageSize) - 1}>
                                                {'>'}
                                            </StyledButton>{' '}
                                            <StyledButton onClick={() => setCurrentPage(Math.ceil(filteredFrames.length / pageSize) - 1)} disabled={currentPage === Math.ceil(filteredFrames.length / pageSize) - 1}>
                                                {'>>'}
                                            </StyledButton>
                                        </div>
                                    </StyledPagination>
                                </>
                            )}
                            {currentStep === 2 && (
                                <>
                                    <PrescriptionContainerLeft>
                                        <FrameCardContainer>
                                            <FrameCard>
                                                <FrameImageContainer>
                                                    <img
                                                        src={`https://www.mcrsafetyeurope.com/${selectedFrameInfo.Image_A}`}
                                                        alt="Frame"
                                                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                                    />
                                                </FrameImageContainer>
                                                <FrameContentContainer>
                                                    <FrameName>{selectedFrameInfo.frameName}</FrameName>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Material: </FrameDetailTitle>{selectedFrameInfo.frameMaterial}
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Type: </FrameDetailTitle>{selectedFrameInfo.frametype}
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Colour: </FrameDetailTitle>{selectedFrameInfo.frameColour}
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Size: </FrameDetailTitle>{selectedFrameInfo.frameSize}
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Temple Length: </FrameDetailTitle>{selectedFrameInfo.frtemplength} mm
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Datum: </FrameDetailTitle>{selectedFrameInfo.frdatum} mm
                                                    </FrameDetail>
                                                </FrameContentContainer>
                                            </FrameCard>
                                        </FrameCardContainer>

                                        <Row>
                                            <FieldContainer>
                                                <SelectWithLabel
                                                    label="Lens Material"
                                                    required={true}
                                                    options={lensMaterials}
                                                    onChange={(e) => setSelectedMaterial(e.target.value)}
                                                    value={selectedMaterial}
                                                    disabled={lensMaterials.length === 2} />
                                            </FieldContainer>
                                            <FieldContainer>
                                                <SelectWithLabel
                                                    label="Lens Types"
                                                    required={true}
                                                    options={(!eyeTicketDetails.requestedDifferentLensType ? defaultLensTypes : lensTypes)}
                                                    onChange={(e) => {
                                                        setSelectedType(e.target.value);
                                                        setSvType(null);
                                                    }}
                                                    value={selectedType}
                                                    missing={missingFields.selectedType} />
                                            </FieldContainer>
                                            <ToggleButton
                                                onToggle={toggleLensOverrides}
                                                initialToggled={eyeTicketDetails.requestedDifferentLensType}>
                                                Lens Change Request
                                            </ToggleButton>
                                        </Row>
                                        <Row>
                                            <FieldContainer>
                                                <SelectWithLabel
                                                    label="Lens Brand"
                                                    required={true}
                                                    options={lensBrands}
                                                    disabled={lensBrands.length === 0}
                                                    missing={missingFields.lenseID}
                                                    onChange={(e) => handleEnteredValues('lenseID', e.target.value)}
                                                    value={eyeTicketDetails.lenseID} />
                                            </FieldContainer>
                                            {svTypes.length > 0 &&
                                                <FieldContainer>
                                                    <SelectWithLabel
                                                        label="SV Type"
                                                        required={true}
                                                        options={svTypes}
                                                        disabled={svTypes.length === 0}
                                                        onChange={(e) => setSvType(e.target.value)}
                                                        value={svType} />
                                                </FieldContainer>}
                                        </Row>
                                        <Row>
                                            <FieldContainer>
                                                <SelectWithLabel
                                                    label="Lens Coating"
                                                    options={lensCoatings}
                                                    onChange={(e) => handleEnteredValues('lenseCoatID', e.target.value)}
                                                    disabled={lensCoatings.length === 0}
                                                    value={eyeTicketDetails.lenseCoatID} />
                                            </FieldContainer>
                                            <ToggleButton
                                                onToggle={() => handleToggle('requestedUnAuthorisedCoating')}
                                                disabled={lensBrands.length === 0}
                                                initialToggled={eyeTicketDetails.requestedUnAuthorisedCoating}>
                                                Request Coating
                                            </ToggleButton>
                                            <FieldContainer>
                                                <SelectWithLabel
                                                    label="Lens Tints"
                                                    options={lensTints}
                                                    disabled={lensTints.length === 0}
                                                    onChange={(e) => handleEnteredValues('lenseTintID', e.target.value)}
                                                    value={eyeTicketDetails.lenseTintID} />
                                            </FieldContainer>
                                            <ToggleButton
                                                onToggle={() => handleToggle('requestedUnAuthorisedTint')}
                                                disabled={lensBrands.length === 0}
                                                initialToggled={eyeTicketDetails.requestedUnAuthorisedTint}>
                                                Request Tint
                                            </ToggleButton>
                                        </Row>
                                        <Row>
                                            <FieldContainer>
                                                <SelectWithLabel
                                                    label="Spectacle Case"
                                                    required={true}
                                                    options={lensCases}
                                                    disabled={lensCases.length === 0}
                                                    onChange={(e) => handleEnteredValues('casesID', e.target.value)}
                                                    value={eyeTicketDetails.casesID} />
                                            </FieldContainer>
                                        </Row>

                                        <Row>
                                            {(eyeTicketDetails.requestedUnAuthorisedTint ||
                                                eyeTicketDetails.requestedUnAuthorisedCoating ||
                                                eyeTicketDetails.requestedNonStandardLens ||
                                                eyeTicketDetails.requestedDifferentLensType) &&
                                                <FieldContainer>
                                                    <RichTextBoxWithLabel
                                                        label="Reason For Unauthorised Item:"
                                                        onChange={(e) => handleEnteredValues('reasonForUnauthorisedItem', e.target.value)}
                                                        value={eyeTicketDetails.reasonForUnauthorisedItem}
                                                        placeholder="Please briefly explain why the patient requires an unauthorised item. This will help the employer to understand why they need the item(s)." />
                                                </FieldContainer>}
                                            <FieldContainer>
                                                <RichTextBoxWithLabel
                                                    label="Additional Notes"
                                                    onChange={(e) => handleEnteredValues('rxNotes', e.target.value)}
                                                    value={eyeTicketDetails.rxNotes} />
                                            </FieldContainer>
                                        </Row>
                                    </PrescriptionContainerLeft>
                                    <PrescriptionContainerTwoColumn>
                                        <PrescriptionTableRedesignTwoColumn
                                            colours={colours}
                                            data={prescriptionMeasures}
                                            orderDetails={eyeTicketDetails}
                                            handleEnteredValues={handleEnteredValues}
                                            selectedLensType={selectedType}
                                            SVType={svType}
                                            missingFields={missingFields} />
                                    </PrescriptionContainerTwoColumn>
                                </>
                            )}
                            {(eyeTicketDetails.requestedDifferentLensType || eyeTicketDetails.requestedNonStandardLens) && currentStep === 3 && (
                                <>
                                    <PrescriptionContainerLeft>
                                        <FrameCardContainer>
                                            <FrameCard>
                                                <FrameImageContainer>
                                                    <img
                                                        src={`https://www.mcrsafetyeurope.com/${selectedFrameInfo.Image_A}`}
                                                        alt="Frame"
                                                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                                    />
                                                </FrameImageContainer>
                                                <FrameContentContainer>
                                                    <FrameName>{selectedFrameInfo.frameName}</FrameName>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Material: </FrameDetailTitle>{selectedFrameInfo.frameMaterial}
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Type: </FrameDetailTitle>{selectedFrameInfo.frametype}
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Colour: </FrameDetailTitle>{selectedFrameInfo.frameColour}
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Size: </FrameDetailTitle>{selectedFrameInfo.frameSize}
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Temple Length: </FrameDetailTitle>{selectedFrameInfo.frtemplength} mm
                                                    </FrameDetail>
                                                    <FrameDetail>
                                                        <FrameDetailTitle>Datum: </FrameDetailTitle>{selectedFrameInfo.frdatum} mm
                                                    </FrameDetail>
                                                </FrameContentContainer>
                                            </FrameCard>
                                        </FrameCardContainer>

                                        <Alert type='warning'>
                                            <b>Important: Review and Action Required</b>
                                            <br />
                                            An unauthorised lens has been selected, or the predefined lens type has been changed. To ensure smooth processing, please select a lens authorised for the EyeTicket order. If the preferred lens is rejected by the authorising company, the standard lens can still be processed, avoiding the need for the patient to return for additional measurements.
                                            <br />
                                            <br />
                                            The prescription details have been auto-populated. Please review and update the prescription to ensure it aligns with the selected lens.</Alert>


                                        <FieldContainer>
                                            <SelectWithLabel
                                                label="Lens Types"
                                                required={true}
                                                options={lensTypes}
                                                onChange={(e) => {
                                                    handleEnteredValues('permittedLenseType', e.target.value);
                                                    setSvType(null);
                                                }}
                                                value={eyeTicketDetails.permittedLenseType}
                                                missing={missingFields.permittedLenseType} />
                                        </FieldContainer>

                                        <FieldContainer>
                                            <SelectWithLabel
                                                label="Lens Brand"
                                                required={true}
                                                options={defaultLensBrands}
                                                disabled={defaultLensBrands.length === 0}
                                                missing={missingFields.permittedLenseID}
                                                onChange={(e) => handleEnteredValues('permittedLenseID', e.target.value)}
                                                value={eyeTicketDetails.permittedLenseID} />
                                        </FieldContainer>
                                        {defaultSvTypes.length > 0 &&
                                            <FieldContainer>
                                                <SelectWithLabel
                                                    label="SV Type"
                                                    required={true}
                                                    options={defaultSvTypes}
                                                    disabled={defaultSvTypes.length === 0}
                                                    onChange={(e) => setDefaultSvType(e.target.value)}
                                                    value={defaultSvType} />
                                            </FieldContainer>}

                                    </PrescriptionContainerLeft>
                                    <PrescriptionContainerTwoColumn>
                                        <StandardPrescriptionTableRedesignTwoColumn
                                            colours={colours}
                                            data={defaultPrescriptionMeasures}
                                            orderDetails={eyeTicketDetails}
                                            handleEnteredValues={handleEnteredValues}
                                            selectedLensType={eyeTicketDetails.permittedLenseType}
                                            SVType={defaultSvType}
                                            missingFields={missingFields}
                                            isDefault={true} />
                                    </PrescriptionContainerTwoColumn>
                                </>
                            )}
                            {currentStep === ((eyeTicketDetails.requestedDifferentLensType || eyeTicketDetails.requestedNonStandardLens) ? 4 : 3) && (
                                <>
                                    <br></br>
                                    <Alert type='warning'>Please check your order requirements carefully. Upon completing this process, your prescription safety order will enter the manufacturing process and the order will be processed to your account. You will be able to view your order history and track each individual order.</Alert>

                                    <p>Sight Test</p>
                                    <Row>
                                        <FieldContainer>
                                            <YesNoSelect
                                                label='Sight Test performed in practice today (to be added to your dispensing fees)'
                                                required={true}
                                                value={eyeTicketDetails.sightTestPerformed}
                                                onChange={(e) => handleEnteredValues('sightTestPerformed', parseInt(e.target.value))}
                                                missing={missingFields.sightTestPerformed}
                                                useNumbers={true} />
                                        </FieldContainer>
                                    </Row>
                                    <Row>
                                        <FieldContainer>
                                            <CustomDatePicker
                                                label="Date Prescription Issued"
                                                required={true}
                                                value={eyeTicketDetails.dateOfSightTest}
                                                onChange={(e) => handleEnteredValues('dateOfSightTest', e.toISOString())}
                                                missing={missingFields.dateOfSightTest}
                                                lockMaxDate={true} />
                                        </FieldContainer>
                                        <FieldContainer>
                                            <SelectWithLabel
                                                label="Sight Test Interval"
                                                options={sightTestDateOptions}
                                                required={true}
                                                value={eyeTicketDetails.sightTestInterval}
                                                onChange={(e) => handleEnteredValues('sightTestInterval', parseInt(e.target.value))}
                                                missing={missingFields.sightTestInterval} />
                                        </FieldContainer>
                                    </Row>

                                    <p>Glasses Delivery Address</p>

                                    <FieldContainer>
                                        <SelectWithLabel
                                            label="Please select the delivery address* "
                                            required={true}
                                            options={deliveryAddressOptions}
                                            onChange={() => setIsDeliveryAlternate(!isDeliveryAlternate)} />
                                    </FieldContainer>
                                    {isDeliveryAlternate &&
                                        <>
                                            <FieldContainer>
                                                <TextInputWithLabel
                                                    label='Ref Name'
                                                    required={true}
                                                    value={eyeTicketDetails.deliveryAddress.companyname}
                                                    onChange={(e) => handleEnteredValues('deliveryAddress.companyname', e.target.value)}
                                                    missing={missingFields.companyname} />
                                            </FieldContainer>
                                            <FieldContainer>
                                                <TextInputWithLabel
                                                    label='Address 1'
                                                    required={true}
                                                    value={eyeTicketDetails.deliveryAddress.coaddress1}
                                                    onChange={(e) => handleEnteredValues('deliveryAddress.coaddress1', e.target.value)}
                                                    missing={missingFields.coaddress1} />
                                            </FieldContainer>
                                            <FieldContainer>
                                                <TextInputWithLabel
                                                    label='Address 2'
                                                    required={true}
                                                    value={eyeTicketDetails.deliveryAddress.coaddress2}
                                                    onChange={(e) => handleEnteredValues('deliveryAddress.coaddress2', e.target.value)}
                                                    missing={missingFields.coaddress2} />
                                            </FieldContainer>
                                            <FieldContainer>
                                                <TextInputWithLabel
                                                    label='Address 3'
                                                    value={eyeTicketDetails.deliveryAddress.coaddress3}
                                                    required={true}
                                                    onChange={(e) => handleEnteredValues('deliveryAddress.coaddress3', e.target.value)}
                                                    missing={missingFields.coaddress3} />
                                            </FieldContainer>
                                            <FieldContainer>
                                                <TextInputWithLabel
                                                    label='Postcode'
                                                    required={true}
                                                    value={eyeTicketDetails.deliveryAddress.copostcode}
                                                    onChange={(e) => handleEnteredValues('deliveryAddress.copostcode', e.target.value)}
                                                    missing={missingFields.copostcode}
                                                    invalidData={invalidPostcode} />
                                            </FieldContainer>
                                        </>
                                    }
                                </>
                            )}
                        </ModalBody>
                    )}
                    <ModalFooter>
                        {isLoading && <Alert type="info">Loading ..</Alert>}
                        {invalidPromoCode && <Alert type='warning'>The entered promotional code is not valid.</Alert>}
                        {Object.values(missingFields).some(isMissing => isMissing) &&
                            (currentStep === 1 ?
                                <Alert type='warning'>Please select a frame from the list above.</Alert>
                                :
                                <Alert type='warning'>Please populate all required fields indicated by an asterisk <span style={{ color: '#880808' }}> *</span></Alert>
                            )
                        }
                        {currentStep > 0 && <Button disabled={isLoading} onClick={() => { setCurrentStep(currentStep - 1); setError(null) }}>Back</Button>}
                        {currentStep < stepTitles.length - 1 ? (
                            <Button
                                onClick={nextStep}
                                title={(!eyeTicketDetails.frameID && "You must select a frame before continuing to the next step.")}
                                disabled={!eyeTicketDetails.managedClinicPatient.employeeRequirements}
                            >
                                {currentStep === 0 && eyeTicketDetails.managedClinicPatient.employeeRequirements ? `Submit As ${eyeTicketDetails.managedClinicPatient.employeeRequirements}` : 'Next'}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleProcessEyeTicket}
                                disabled={isLoading}>Submit Order</Button>)}
                        <CancelButton disabled={isLoading} onClick={() => {
                            setEyeTicketDetails(cloneDefaultEyeTicket());
                            showHideModal();
                        }}>Cancel</CancelButton>
                    </ModalFooter>
                </StyledModal>
            </Modal>
        </>
    );
}
